import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";
import { insertWithWriteSource, type WriteSource } from "./writeSource";
import { indexQuoteRow } from "./knowledge";
import { createQuoteLineItems, listQuoteLineItems } from "./quoteLineItems";

export type Quote = Tables<"quotes">;
export type QuoteInsert = Insert<"quotes">;
export type QuoteUpdate = Update<"quotes">;

/** Globális listához: kapcsolódó feladat fázisa (Procore „bid → work item” navigáció). */
export type QuoteWithTaskPhase = Tables<"quotes"> & {
  tasks: { phase_id: string | null } | null;
};

export async function listQuotes(): Promise<QuoteWithTaskPhase[]> {
  const { data, error } = await supabase
    .from("quotes")
    .select("*, tasks(phase_id)")
    .order("created_at", { ascending: false });
  throwIfError(error);
  return excludeTestData(data) as QuoteWithTaskPhase[];
}

export async function listQuotesByTask(taskId: string): Promise<Quote[]> {
  const { data, error } = await supabase
    .from("quotes")
    .select("*")
    .eq("task_id", taskId)
    .order("created_at", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function getQuote(id: string): Promise<Quote> {
  const { data, error } = await supabase.from("quotes").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Quote not found");
}

function scheduleQuoteIndex(row: Quote): void {
  void indexQuoteRow(row).catch(() => {
    // Indexelés best-effort — a mentés már sikerült.
  });
}

function withoutUnknownValidity(message: string, values: Record<string, unknown>): Record<string, unknown> | null {
  if (!/validity_unknown/i.test(message)) return null;
  if (!/schema cache|could not find|does not exist/i.test(message)) return null;
  const rest = { ...values };
  delete rest.validity_unknown;
  return rest;
}

export async function createQuote(values: QuoteInsert, source: WriteSource = "ui"): Promise<Quote> {
  const payload = values as unknown as Record<string, unknown>;
  try {
    const row = await insertWithWriteSource<Quote>("quotes", payload, source);
    scheduleQuoteIndex(row);
    return row;
  } catch (e) {
    const retry = withoutUnknownValidity(e instanceof Error ? e.message : "", payload);
    if (!retry) throw e;
    const row = await insertWithWriteSource<Quote>("quotes", retry, source);
    scheduleQuoteIndex(row);
    return row;
  }
}

export async function updateQuote(id: string, values: QuoteUpdate): Promise<Quote> {
  const payload = values as unknown as Record<string, unknown>;
  let { data, error } = await supabase.from("quotes").update(values).eq("id", id).select("*").single();
  const retry = error ? withoutUnknownValidity(error.message, payload) : null;
  if (retry) {
    ({ data, error } = await supabase
      .from("quotes")
      .update(retry as QuoteUpdate)
      .eq("id", id)
      .select("*")
      .single());
  }
  throwIfError(error);
  const row = assertData(data, "Quote not found");
  scheduleQuoteIndex(row);
  if (values.status === "accepted" || row.status === "accepted") {
    // F2.1 — lekötés + ütemezés (best-effort; a mentés már sikerült)
    void import("./commitments")
      .then(({ ensureCommitmentFromAcceptedQuote }) => ensureCommitmentFromAcceptedQuote(row.id))
      .catch(() => {});
  }
  return row;
}

/** Új revízió: másolat, quote_revision_of az előzőre mutat, státusz függőben. */
export async function createQuoteRevision(sourceId: string): Promise<Quote> {
  const src = await getQuote(sourceId);
  const lines = await listQuoteLineItems(sourceId);
  const created = await createQuote({
    title: `${src.title} (rev)`,
    title_hu: src.title_hu ? `${src.title_hu} (rev)` : null,
    contact_id: src.contact_id,
    task_id: src.task_id,
    variant_group: src.variant_group,
    currency: src.currency,
    amount: src.amount,
    amount_net: src.amount_net,
    amount_gross: src.amount_gross,
    vat_rate: src.vat_rate,
    valid_until: src.valid_until,
    validity_unknown: src.validity_unknown ?? false,
    status: "pending",
    quote_revision_of: src.id,
    notes: src.notes,
    pallet_deposit: src.pallet_deposit,
    delivery_window: src.delivery_window,
    delivery_window_start: src.delivery_window_start ?? null,
    delivery_window_end: src.delivery_window_end ?? null,
    includes_delivery: src.includes_delivery ?? null,
    includes_unloading: src.includes_unloading ?? null,
    includes_crane: src.includes_crane ?? null,
    includes_assembly: src.includes_assembly ?? null
  });
  await createQuoteLineItems(
    lines.map((line) => ({
      quote_id: created.id,
      name: line.name,
      unit: line.unit,
      quantity: line.quantity,
      unit_price_net: line.unit_price_net,
      total_net: line.total_net,
      vat_rate: line.vat_rate,
      total_gross: line.total_gross,
      item_type: line.item_type,
      excluded_from_comparison: line.excluded_from_comparison,
      sort_index: line.sort_index
    }))
  );
  return created;
}

export async function deleteQuote(id: string): Promise<void> {
  const { error } = await supabase.from("quotes").delete().eq("id", id);
  throwIfError(error);
}

export const quotesQueryOptions = () =>
  queryOptions({
    queryKey: qk.quotes,
    queryFn: listQuotes
  });

export const quotesByTaskQueryOptions = (taskId: string) =>
  queryOptions({
    queryKey: [...qk.quotes, { taskId }] as const,
    queryFn: () => listQuotesByTask(taskId)
  });

export const quoteQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.quote(id),
    queryFn: () => getQuote(id)
  });

